// frontend/src/modules/reports/components/ReportShell.tsx
//
// Phase 4 (Reports Frontend), Task 1. The ONE chrome implementation every
// report page (all 14) reuses: title/eyebrow header, date-range presets
// (today | this week | this month | last month | custom, with validation),
// a filter slot for report-specific controls, the CSV download button, and
// honest loading/error/empty/no-access states.
//
// ReportShell owns the date-range UI + validation and reports the resolved
// range up via `onRangeChange` (called once on mount and again whenever the
// preset/custom dates change) — the caller owns data-fetching (useApi keyed
// off that range) since only the caller knows its report's params.

import * as React from 'react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { PageSkeleton } from '../../../design-system/components/PageSkeleton';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { VISUAL } from '../../../design-system/visual';
import { FileDown } from 'lucide-react';
import { motion, LayoutGroup } from 'framer-motion';
import { toast } from 'sonner';

export type ReportPreset = 'today' | 'this_week' | 'this_month' | 'last_month' | 'custom';

export interface ReportRange { from: string; to: string; }

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isoDay(d: Date): string { return d.toISOString().slice(0, 10); }

/** Same UTC-calendar-date preset math the existing projects/ReportsPage uses. */
export function presetRange(preset: ReportPreset, custom?: ReportRange): ReportRange {
  const now = new Date();
  const today = isoDay(now);
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'this_week') {
    const day = now.getUTCDay();
    const monOffset = (day + 6) % 7;
    const monday = new Date(now);
    monday.setUTCDate(now.getUTCDate() - monOffset);
    return { from: isoDay(monday), to: today };
  }
  if (preset === 'this_month') {
    const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { from: isoDay(first), to: today };
  }
  if (preset === 'last_month') {
    const lastMonthEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
    const lastMonthStart = new Date(Date.UTC(lastMonthEnd.getUTCFullYear(), lastMonthEnd.getUTCMonth(), 1));
    return { from: isoDay(lastMonthStart), to: isoDay(lastMonthEnd) };
  }
  // custom
  return custom ?? { from: today, to: today };
}

function validateCustomRange(from: string, to: string): string | null {
  if (!DATE_RE.test(from) || Number.isNaN(new Date(`${from}T00:00:00Z`).getTime())) {
    return 'Start date must be a valid date.';
  }
  if (!DATE_RE.test(to) || Number.isNaN(new Date(`${to}T00:00:00Z`).getTime())) {
    return 'End date must be a valid date.';
  }
  if (from > to) return 'Start date must be on or before the end date.';
  return null;
}

interface ReportShellProps {
  eyebrow: string;
  title: string;
  description?: string;
  /** Initial preset (default 'this_month'). Reports with no date range
   * (live-roster, invoice-aging) should pass `noDateRange`. */
  initialPreset?: ReportPreset;
  /** Suppresses the whole date-range control block (e.g. live-roster,
   * invoice-aging, which have no from/to). */
  noDateRange?: boolean;
  onRangeChange?: (range: ReportRange) => void;
  /** Extra report-specific controls (employee select, type chips, …). */
  filters?: React.ReactNode;
  /** When false, the shell renders NoAccessState instead of filters/children
   * — used for the client-side visibility fallback and for a 403 that slips
   * through to the page. */
  access?: boolean;
  accessDescription?: string;
  csv?: { filename: string; onDownload: () => Promise<void> } | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  children?: React.ReactNode;
}

export function ReportShell({
  eyebrow, title, description, initialPreset = 'this_month', noDateRange,
  onRangeChange, filters, access = true, accessDescription, csv,
  loading, error, onRetry, isEmpty, emptyTitle = 'No data for this period',
  emptyDescription = 'Try a different date range or filter.', children,
}: ReportShellProps) {
  const [preset, setPreset] = React.useState<ReportPreset>(initialPreset);
  const defaultCustom = React.useMemo(() => presetRange('this_month'), []);
  const [customFrom, setCustomFrom] = React.useState(defaultCustom.from);
  const [customTo, setCustomTo] = React.useState(defaultCustom.to);
  const [rangeError, setRangeError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState(false);

  const onRangeChangeRef = React.useRef(onRangeChange);
  onRangeChangeRef.current = onRangeChange;

  React.useEffect(() => {
    if (noDateRange) return;
    if (preset === 'custom') {
      const err = validateCustomRange(customFrom, customTo);
      setRangeError(err);
      if (err) return;
      onRangeChangeRef.current?.({ from: customFrom, to: customTo });
      return;
    }
    setRangeError(null);
    const range = presetRange(preset);
    onRangeChangeRef.current?.(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo, noDateRange]);

  const currentRange = preset === 'custom' ? { from: customFrom, to: customTo } : presetRange(preset);

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={
          !noDateRange && access ? (
            <div className="flex flex-wrap items-center gap-2">
              <LayoutGroup id={`reportPreset-${title}`}>
                <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
                  <PresetButton active={preset === 'today'} onClick={() => setPreset('today')}>Today</PresetButton>
                  <PresetButton active={preset === 'this_week'} onClick={() => setPreset('this_week')}>This week</PresetButton>
                  <PresetButton active={preset === 'this_month'} onClick={() => setPreset('this_month')}>This month</PresetButton>
                  <PresetButton active={preset === 'last_month'} onClick={() => setPreset('last_month')}>Last month</PresetButton>
                  <PresetButton active={preset === 'custom'} onClick={() => setPreset('custom')}>Custom</PresetButton>
                </div>
              </LayoutGroup>
              {csv && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={downloading || !!rangeError}
                  className="rounded-full h-9 px-4 text-[13px] font-semibold gap-1.5"
                  onClick={async () => {
                    setDownloading(true);
                    try { await csv.onDownload(); }
                    catch (err: any) { toast.error('Export failed', { description: err?.message ?? String(err) }); }
                    finally { setDownloading(false); }
                  }}
                >
                  <FileDown className="h-3.5 w-3.5" />
                  {downloading ? 'Exporting…' : 'Export CSV'}
                </Button>
              )}
            </div>
          ) : csv && access ? (
            <Button
              type="button"
              variant="outline"
              disabled={downloading}
              className="rounded-full h-9 px-4 text-[13px] font-semibold gap-1.5"
              onClick={async () => {
                setDownloading(true);
                try { await csv.onDownload(); }
                catch (err: any) { toast.error('Export failed', { description: err?.message ?? String(err) }); }
                finally { setDownloading(false); }
              }}
            >
              <FileDown className="h-3.5 w-3.5" />
              {downloading ? 'Exporting…' : 'Export CSV'}
            </Button>
          ) : null
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-6xl space-y-6">
        {!access ? (
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState description={accessDescription} />
          </div>
        ) : (
          <>
            {!noDateRange && preset === 'custom' && (
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-[12px] font-medium text-muted-foreground">
                  From
                  <Input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="h-9 w-40"
                  />
                </label>
                <label className="flex flex-col gap-1 text-[12px] font-medium text-muted-foreground">
                  To
                  <Input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="h-9 w-40"
                  />
                </label>
                {rangeError && (
                  <p role="alert" className="text-[13px] text-destructive font-medium pb-2">{rangeError}</p>
                )}
              </div>
            )}

            {filters && <div className="flex flex-wrap items-center gap-2">{filters}</div>}

            {loading ? (
              <PageSkeleton />
            ) : error ? (
              <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                <ErrorState title="Couldn't load this report" description={error} onRetry={onRetry} />
              </div>
            ) : isEmpty ? (
              <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                <EmptyState title={emptyTitle} description={emptyDescription} />
              </div>
            ) : (
              children
            )}
          </>
        )}
      </div>
      {/* Exposed for tests/consumers that want the currently-resolved range
          without re-deriving preset math (e.g. showing "as of" copy). */}
      <span data-testid="report-shell-current-range" hidden>{JSON.stringify(currentRange)}</span>
    </div>
  );
}

function PresetButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'relative inline-flex items-center px-3.5 h-8 text-[12px] font-semibold tracking-[-0.005em] transition-colors',
        active ? 'text-zinc-950' : 'text-muted-foreground hover:text-foreground',
      ].join(' ')}
    >
      {active && (
        <motion.span
          layoutId="reportShellPresetActive"
          className="absolute inset-0 -z-10 rounded-full bg-saffron"
          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
        />
      )}
      <span className="relative">{children}</span>
    </button>
  );
}
